'use client';

import { useState, useRef, useEffect } from 'react';
import { UserDetails } from '@/utils/helpers';
import { CommentWithUser } from './types';
import { voteComment, deleteComment } from './api';
import { ThumbsUp, ThumbsDown, Edit2, Trash2, Ellipsis, Flag } from 'lucide-react';
import { ActionModal } from '@/components/ui/ActionModal/ActionModal';
import { useRouter } from 'next/navigation';
import Button from '@/components/ui/Button';
import s from '@/components/ui/Navbar/Navbar.module.css';
import { ReportModal } from './ReportModal';
import { isModerator } from '@/utils/moderation';
import { FilledThumbsUp, FilledThumbsDown } from './Icons';
import { analytics } from '@/lib/analytics';

interface CommentActionsProps {
  comment: CommentWithUser;
  userDetails: UserDetails | null;
  isOwner: boolean;
  canReply: boolean;
  onReply: () => void;
  onEdit: () => void;
  onDelete?: () => void;
  onVoteUpdate?: (commentId: number, upvotes: number, downvotes: number) => void; // Legacy callback, will be deprecated
}

export function CommentActions({
  comment,
  userDetails,
  isOwner,
  canReply,
  onReply,
  onEdit,
  onDelete,
  onVoteUpdate,
}: CommentActionsProps) {
  const [userVote, setUserVote] = useState<number | null>(comment.current_user_vote || null);
  const [netVotes, setNetVotes] = useState(comment.net_votes ?? 0);
  const [isVoting, setIsVoting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const isUserModerator = userDetails?.id ? isModerator(userDetails.id) : false;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleVote = async (voteType: 1 | -1) => {
    // Redirect to login if not authenticated
    if (!userDetails) {
      router.push('/signin/email');
      return;
    }

    if (isVoting) return;
    const newVoteType = userVote === voteType ? null : voteType;

    // Store previous state for rollback
    const previousUserVote = userVote;
    const previousNetVotes = netVotes;

    // OPTIMISTIC UPDATE - Update UI immediately
    setUserVote(newVoteType);
    let newNetVotes = netVotes;

    // Remove previous vote if it existed
    if (previousUserVote === 1) {
      newNetVotes -= 1;
    } else if (previousUserVote === -1) {
      newNetVotes += 1;
    }

    // Add new vote if it's not null
    if (newVoteType === 1) {
      newNetVotes += 1;
    } else if (newVoteType === -1) {
      newNetVotes -= 1;
    }

    setNetVotes(newNetVotes);
    setIsVoting(true);
    try {
      const result = await voteComment({
        commentId: comment.id!,
        voteType: newVoteType,
      });

      // Sync with server response (in case of any discrepancies)
      setUserVote(result.userVote);
      setNetVotes(result.netVotes);
    } catch (error) {
      console.error('Error voting:', error);

      // ROLLBACK - Restore previous state on error
      setUserVote(previousUserVote);
      setNetVotes(previousNetVotes);

      // TODO: Show error toast to user
    } finally {
      setIsVoting(false);
    }

    analytics.capture('comment.voted', {
      comment_id: comment.id,
      vote_type: newVoteType,
    });
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      if (isUserModerator && !isOwner) {
        // Use moderation route for moderators deleting other users' comments
        const response = await fetch('/api/comments/moderate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            commentId: comment.id,
            action: 'delete',
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to moderate comment');
        }
      } else {
        // Use regular delete route for owners deleting their own comments
        await deleteComment({ commentId: comment.id! });
      }
      onDelete?.();
    } catch (error) {
      console.error('Error deleting comment:', error);
      // TODO: Show error toast to user
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex items-center gap-6 text-sm">
      {/* Voting - Always show, redirect to login if not authenticated */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => handleVote(1)}
          disabled={isVoting}
          className={`${userVote === 1 ? 'text-white' : 'text-zinc-400 hover:text-white'}`}
        >
          {userVote === 1 ? <FilledThumbsUp /> : <ThumbsUp size={20} />}
        </button>

        <span className={`text-zinc-400 min-w-[14px] text-center`}>
          {netVotes.toLocaleString()}
        </span>

        <button
          onClick={() => handleVote(-1)}
          disabled={isVoting}
          className={`${userVote === -1 ? 'text-white' : 'text-zinc-400 hover:text-white'}`}
        >
          {userVote === -1 ? <FilledThumbsDown /> : <ThumbsDown size={20} />}
        </button>
      </div>

      {/* Reply - Show for all users, redirect to login if not authenticated */}
      {canReply && (
        <button
          onClick={() => {
            if (!userDetails) {
              router.push('/signin/email');
              return;
            }
            onReply();
          }}
          className="flex items-center font-bold gap-2 text-zinc-400 hover:text-white transition-colors"
        >
          Reply
        </button>
      )}

      {/* Menu actions - Show for authenticated users */}
      {userDetails && (
        <div className="relative ml-auto" ref={menuRef}>
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="p-2 text-zinc-400 hover:text-white transition-colors"
          >
            <Ellipsis size={20} />
          </button>

          <nav
            className={`${
              isMenuOpen ? 'block' : 'hidden'
            } absolute z-50 right-0 top-8 bg-zinc-900 text-white rounded-lg shadow-lg border border-zinc-500 border-opacity-50 min-w-[160px]`}
          >
            <div className="flex flex-col p-3">
              {/* Owner-only actions */}
              {isOwner && (
                <button
                  onClick={() => {
                    onEdit();
                    setIsMenuOpen(false);
                  }}
                  className={`${s.link} w-full whitespace-nowrap gap-2 py-2 px-3 hover:bg-zinc-800 rounded-lg`}
                >
                  <Edit2 size={20} className="flex-shrink-0" />
                  Edit
                </button>
              )}

              {/* Delete option - Show for owners or moderators */}
              {(isOwner || isUserModerator) && (
                <ActionModal
                  trigger={
                    <span
                      className={`${s.link} w-full whitespace-nowrap gap-2 py-2 px-3 hover:bg-zinc-800 rounded-lg`}
                    >
                      <Trash2 size={20} className="flex-shrink-0" />
                      Delete
                    </span>
                  }
                  title="Delete Comment"
                  message={
                    isUserModerator && !isOwner
                      ? 'Are you sure you want to moderate this comment? This will mark it as deleted.'
                      : 'Are you sure you want to delete this comment? This action cannot be undone.'
                  }
                  primaryAction={
                    <Button
                      className="w-full text-white"
                      style={{ background: 'red', color: 'white' }}
                      onClick={handleDelete}
                      variant="slim"
                      disabled={isDeleting}
                      loading={isDeleting}
                      tracking={{
                        event: 'comment.deleted',
                        properties: {
                          is_moderator: isUserModerator && !isOwner,
                        },
                      }}
                    >
                      {isUserModerator && !isOwner ? 'Moderate' : 'Delete'}
                    </Button>
                  }
                  cancelText="Cancel"
                />
              )}

              {/* Report option - Show for all authenticated users */}
              <ReportModal
                comment={comment}
                trigger={
                  <span
                    className={`${s.link} w-full whitespace-nowrap gap-2 py-2 px-3 hover:bg-zinc-800 rounded-lg`}
                  >
                    <Flag size={20} className="flex-shrink-0" />
                    Report
                  </span>
                }
              />
            </div>
          </nav>
        </div>
      )}
    </div>
  );
}
